import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useSnackbar } from 'notistack';
import {
  Button,
  Grid,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox
} from '@material-ui/core';
import { makeStyles, withStyles, createStyles } from '@material-ui/core/styles';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import GetAppIcon from '@material-ui/icons/GetApp';
import { getFileDownload, getFilesZip } from '../../apicalls/cloud/shareFile';
import { FileList } from './FileList';
import { AddModal } from './AddModal';
import { progressFlgState, btnColor } from '../../GlobalState';
import { checkResData } from '../../utils/common';
import type {
  TSharedFileList,
  TSharedFile
} from '../../types/cloudStorage/CloudFileList';

type Tprops = {
  sharedFileList: TSharedFileList;
  getSharedFileList: () => void;
};

//書類一覧用フォルダリスト
export const FolderField: React.FC<Tprops> = (props: Tprops) => {
  const { sharedFileList, getSharedFileList } = props;

  const classes = useStyles();
  const [buttonColor] = useRecoilState(btnColor);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedFiles, setSelectedFiles] = useState<TSharedFile[]>([]);
  const [AllChecked, setAllChecked] = useState<boolean>(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [, setProgressFlg] = useRecoilState<boolean>(progressFlgState);

  const [disableBtn, setDisableBtn] = useState<boolean>(true);
  useEffect(() => {
    if (selectedFiles.length === 0) {
      setDisableBtn(true);
    } else {
      setDisableBtn(false);
    }
  }, [selectedFiles]);

  /**
   * 選択ファイルを追加
   * @param file
   */
  const addSelectedFiles = (file: TSharedFile) => {
    if (!selectedFiles.includes(file)) {
      setSelectedFiles([...selectedFiles, file]);
    }
  };

  /**
   * 選択ファイルを追加(全件)
   */
  const addAllSelectedFiles = () => {
    setSelectedFiles([]);
    setSelectedFiles(sharedFileList.files);
  };

  /**
   * 選択ファイルを削除
   * @param file
   */
  const removeSelectedFiles = (file: TSharedFile) => {
    if (selectedFiles.includes(file)) {
      const newSelectedFileIds = selectedFiles.filter(f => f.id !== file.id);
      setSelectedFiles(newSelectedFileIds);
    }
  };

  /**
   * 選択ファイルを削除(全件)
   */
  const removeAllSelectedFiles = () => {
    setSelectedFiles([]);
  };

  /**
   * 全件チェック
   */
  const handleAllCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked: boolean = event.target.checked;
    if (checked) {
      addAllSelectedFiles();
    } else {
      removeAllSelectedFiles();
    }
    setAllChecked(checked);
  };

  /**
   * zipダウンロード
   */
  const downloadFileZip = async () => {
    if (selectedFiles.length <= 0) return;
    setProgressFlg(true);

    if (selectedFiles.length === 1) {
      await getFileDownload(String(selectedFiles[0].id), true).then(
        (resData: any) => {
          checkResData(resData, enqueueSnackbar).then((data: any) => {
            // 書類ダウンロード
            let bin = atob(data.data.replace(/^.*,/, ''));

            let buffer = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) {
              buffer[i] = bin.charCodeAt(i);
            }

            let fileData = new File([buffer.buffer], data.data, {
              type: data.file_format
            });

            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(fileData);
            const fileName = selectedFiles[0].name;
            downloadLink.download = fileName;
            downloadLink.click();
          });
        }
      );
    } else {
      const params: string = selectedFiles
        .map(file => {
          return file.id;
        })
        .join(',');

      await getFilesZip(params).then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          // 書類ダウンロード
          let bin = atob(data.data.replace(/^.*,/, ''));

          let buffer = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) {
            buffer[i] = bin.charCodeAt(i);
          }

          let fileData = new File([buffer.buffer], data.data, {
            type: data.file_format
          });

          const downloadLink = document.createElement('a');
          downloadLink.href = URL.createObjectURL(fileData);
          const fileName = 'document-list.zip';
          downloadLink.download = fileName;
          downloadLink.click();
        });
      });
    }

    // selectedFilesをリセット
    setSelectedFiles([]);
    setProgressFlg(false);
  };

  return (
    <>
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #f5f5f5',
            borderBottom: '1px solid #f5f5f5',
            padding: '30px 20px'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 36
            }}
          >
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '20px'
                }}
              >
                <FolderOpenIcon style={{ marginRight: '8px' }} />
                <span style={{ fontWeight: 'bold' }}>
                  {sharedFileList.folder_name}
                </span>
              </div>
              <div style={{ marginLeft: '12px', color: 'gray' }}>
                {sharedFileList.construct_name}
              </div>
            </div>

            {/* フォルダ内ボタン */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center'
              }}
            >
              {selectedFiles.length !== 0 && (
                <div style={{ color: 'red', marginRight: 24 }}>
                  {selectedFiles.length}件選択中
                </div>
              )}
              <div style={{ marginRight: 14 }}>
                <div>選択した資料を</div>
              </div>
              <Button
                style={{
                  backgroundColor: buttonColor,
                  color: '#fff'
                }}
                variant='contained'
                color='secondary'
                onClick={downloadFileZip}
                disabled={disableBtn}
              >
                <GetAppIcon />
                <span style={{ marginLeft: 4 }}>ダウンロード</span>
              </Button>
              {sharedFileList.uploadable && (
                <div
                  style={{
                    marginLeft: 8,
                    paddingLeft: 8,
                    borderLeft: '1px solid #cfcfcf'
                  }}
                >
                  <Button
                    style={{
                      backgroundColor: '#007dff'
                    }}
                    onClick={() => setAddModalOpen(true)}
                    variant='contained'
                    color='secondary'
                  >
                    <NoteAddIcon />
                    <span style={{ marginLeft: 4 }}>資料を追加</span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {sharedFileList.files.length === 0 ? (
            <div>フォルダに書類がありません</div>
          ) : (
            <TableContainer component={Paper}>
              <Table className={classes.main} aria-label='customized table'>
                <TableHead>
                  <TableRow>
                    <StyledTableCell className={classes.table} align='center'>
                      <Checkbox
                        checked={AllChecked}
                        onChange={handleAllCheck}
                        color='default'
                      />
                    </StyledTableCell>
                    <StyledTableCell className={classes.table} align='center'>
                      書類名
                    </StyledTableCell>
                    <StyledTableCell className={classes.table} align='center'>
                      資料種別
                    </StyledTableCell>
                    <StyledTableCell className={classes.table} align='center'>
                      ファイルサイズ
                    </StyledTableCell>
                    <StyledTableCell className={classes.table} align='center'>
                      日付
                    </StyledTableCell>
                    <StyledTableCell
                      className={classes.table}
                      align='center'
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.table}
                      align='center'
                    ></StyledTableCell>
                    <StyledTableCell
                      className={classes.table}
                      align='center'
                    ></StyledTableCell>
                  </TableRow>
                </TableHead>

                {sharedFileList.files.map(file => (
                  <FileList
                    key={file.id}
                    file={file}
                    addSelectedFiles={() => addSelectedFiles(file)}
                    removeSelectedFiles={() => removeSelectedFiles(file)}
                    selectedFiles={selectedFiles}
                    getSharedFileList={getSharedFileList}
                    isGuestFile={
                      sharedFileList.share_file_guest_id ===
                      file.created_guest_id
                    }
                  />
                ))}
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      {/* 資料追加モーダル */}
      {sharedFileList && (
        <AddModal
          addModalOpen={addModalOpen}
          setAddModalOpen={setAddModalOpen}
          constructId={sharedFileList.construct_id}
          fileFolderId={sharedFileList.folder_id}
          getSharedFileList={getSharedFileList}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      minWidth: 120,
      backgroundColor: '#FFFFFF'
    },
    formControl1: {
      minWidth: 120,
      height: 30,
      borderRadius: 5,
      borderWidth: 1,
      paddingLeft: 5
    },
    main: {
      minWidth: 700
    },
    table: {
      backgroundColor: `#0099CC !important`,
      color: '#FFFFFF',
      padding: 10,
      fontWeight: 'bold',
      fontSize: 16
    },
    input: {
      borderColor: '#CCCCCC',
      padding: 5,
      margin: 10
    },
    table1: {
      padding: 8
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 15
    },
    modal: {
      // モーダルを中央に
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    popoverButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      borderBottom: '1px solid rgb(245, 245, 245)',
      padding: '20px 40px'
    }
  })
);

// スタイル関係
const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    fontSize: 16
  }
}))(TableCell);
